import { createGlobalStyle } from 'styled-components'

const Global = createGlobalStyle`
  :root {
    /* Brand Colors */
    --color-primary: #97CA6F;
    --color-secondary: #C2ECAA;
    --color-main-gray: #2A2A2A;
    
    /* Background Colors */
    --color-bg-main: #101010;
    --color-bg-card: #101010;
    --color-bg-sidebar: #2A2A2A;
    --color-bg-footer: #2A2A2A;
    --color-bg-input: #f1f4f7;
    
    /* Text Colors */
    --color-text-primary: #fff;
    --color-text-secondary: #a5acc2;
    --color-text-disabled: #666;
    --color-input-text: #4b5882;  /* Dark text for light input backgrounds */
    
    /* Accent Colors */
    --color-success: #00c866;
    --color-warning: #f7b500;
    --color-error: #ff4140;
    --color-info: #00bfff;
    
    /* Interactive States */
    --color-hover: #97CA6F;
    --color-sidebar-hover: #6A9949;
    --color-focus: #97CA6F;
    --color-active: #7AB85C;
    
    /* Legacy Colors (for gradual migration) */
    --color-legacy-primary: #244d85;
    --color-legacy-secondary: #00bfff;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
    flex-shrink: 0;
  }

  html {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-text-size-adjust: 100%; 
    -ms-text-size-adjust: 100%;
    -webkit-tap-highlight-color: rgba(black, 0);
  }

  body {
    margin: 0;
    font-family: 'Roboto', Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: var(--color-text-primary);
    font-weight: 400;
    background-color: var(--color-bg-main);
  }

  a {
    text-decoration: none;
  }

  hr {
    box-sizing: content-box; 
    height: 0; 
    overflow: visible;
  }

  input,
  button,
  select,
  optgroup,
  textarea {
    margin: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  button,
  input {
    overflow: visible;
  }

  button,
  select {
    text-transform: none;
  }

  input[type="radio"],
  input[type="checkbox"] {
    box-sizing: border-box; 
    padding: 0;
  }

  button::-moz-focus-inner,
  [type="button"]::-moz-focus-inner,
  [type="reset"]::-moz-focus-inner,
  [type="submit"]::-moz-focus-inner {
    padding: 0;
    border-style: none;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace;
  }

  .appWrapper {
    display: flex;
    min-height: 100vh;
    background-color: var(--color-bg-main);
  }

  /* Enhanced link styling */
  a {
    text-decoration: none;
    color: var(--color-secondary);
    transition: color 0.2s ease;

    &:hover {
      color: var(--color-primary);
    }

    &:focus {
      outline: 2px solid var(--color-focus);
      outline-offset: 2px;
    }
  }

  /* Enhanced button base styling */
  button {
    cursor: pointer;
    border: none;
    background: none;
    font-family: inherit;
    transition: all 0.2s ease;

    &:focus {
      outline: 2px solid var(--color-focus);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  /* Enhanced form element styling */
  input,
  textarea,
  select {
    background-color: var(--color-bg-input);
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--color-input-text, #4b5882);
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: var(--color-primary);
      box-shadow: 0 0 0 2px rgba(151, 202, 111, 0.2);
    }

    &::placeholder {
      color: var(--color-text-secondary);
    }

    &:disabled {
      background-color: #f5f5f5;
      color: var(--color-text-disabled);
      cursor: not-allowed;
    }
  }

  /* Scrollbar styling for webkit browsers */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--color-bg-main);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--color-text-secondary);
    border-radius: 4px;

    &:hover {
      background: var(--color-secondary);
    }
  }

  /* Selection styling */
  ::selection {
    background-color: var(--color-primary);
    color: var(--color-bg-main);
  }

  ::-moz-selection {
    background-color: var(--color-primary);
    color: var(--color-bg-main);
  }

  .tooltip-link {
    cursor: pointer;
    display: block;
    color: var(--color-text-primary);
    font-size: 10px;
    font-weight: 900;
    text-decoration: none;
    text-transform: uppercase;
    background-color: transparent;
    transition: 0.2s;

    &:hover {
      color: var(--color-secondary);
    }

    &:focus {
      outline: 2px solid var(--color-focus);
      outline-offset: 2px;
    }
  }

  /* Global utility classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .text-primary {
    color: var(--color-text-primary) !important;
  }

  .text-secondary {
    color: var(--color-text-secondary) !important;
  }

  .text-success {
    color: var(--color-success) !important;
  }

  .text-warning {
    color: var(--color-warning) !important;
  }

  .text-error {
    color: var(--color-error) !important;
  }

  .bg-primary {
    background-color: var(--color-primary) !important;
  }

  .bg-secondary {
    background-color: var(--color-secondary) !important;
  }

  .bg-main {
    background-color: var(--color-bg-main) !important;
  }

  .bg-card {
    background-color: var(--color-bg-card) !important;
  }

  /* Focus management for accessibility */
  .focus-visible {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
  }

  /* Skip link for accessibility */
  .skip-link {
    position: absolute;
    top: -40px;
    left: 6px;
    background: var(--color-primary);
    color: var(--color-bg-main);
    padding: 8px;
    text-decoration: none;
    border-radius: 4px;
    z-index: 1000;
    font-weight: bold;

    &:focus {
      top: 6px;
    }
  }

  ol, ul {
    padding: revert;
  }

  /* Global Alert Styles */
  .global-alert {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    margin: 10px 0;
    border-radius: 8px;
    background-color: var(--color-bg-sidebar);
    border: 1px solid var(--color-text-secondary);
    color: var(--color-text-primary);
    font-size: 14px;
    animation: slideIn 0.3s ease;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .global-alert.success {
    border-color: var(--color-success);
    background-color: rgba(0, 200, 102, 0.1);
  }

  .global-alert.warning {
    border-color: var(--color-warning);
    background-color: rgba(247, 181, 0, 0.1);
  }

  .global-alert.error {
    border-color: var(--color-error);
    background-color: rgba(255, 65, 64, 0.1);
  }

  .global-alert.info {
    border-color: var(--color-info);
    background-color: rgba(0, 191, 255, 0.1);
  }

  .global-alert-body {
    flex: 1;
    padding: 0 16px;
  }

  .global-alert .delete {
    width: 24px;
    height: 24px;
    background-color: transparent;
    border: none;
    cursor: pointer;
    position: relative;
    border-radius: 50%;
    transition: background-color 0.2s ease;
  }

  .global-alert .delete:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }

  .global-alert .delete::before,
  .global-alert .delete::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 14px;
    height: 2px;
    background-color: var(--color-text-primary);
    border-radius: 1px;
  }

  .global-alert .delete::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  .global-alert .delete::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  .global-alert .delete:focus {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
  }

  .ql-editor li[data-list=ordered].ql-indent-1 > .ql-ui:before,
  .ql-editor li[data-list=ordered].ql-indent-4 > .ql-ui:before,
  .ql-editor li[data-list=ordered].ql-indent-7 > .ql-ui:before {
    content: counter(list-1, decimal) '. ';
  }

  .ql-editor li[data-list=ordered].ql-indent-2 > .ql-ui:before,
  .ql-editor li[data-list=ordered].ql-indent-5 > .ql-ui:before,
  .ql-editor li[data-list=ordered].ql-indent-8 > .ql-ui:before {
    content: counter(list-2, decimal) '. ';
  }
`

export default Global
